import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import {
  AffiliateApplication,
  AffiliateApplicationStatus,
  ApproveAffiliateApplicationDto,
} from '../domain/affiliate-application-types';
import { affiliateApplicationService } from '../infra/affiliateApplicationService';
import { transformAffiliateApplication } from '../infra/mappers';

interface ApproveAffiliateApplicationParams {
  projectId: string;
  applicationId: string;
  sourceStatus: AffiliateApplicationStatus;
  dto?: ApproveAffiliateApplicationDto;
}

interface UseApproveAffiliateApplicationResult {
  approveApplication: (params: ApproveAffiliateApplicationParams) => void;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
}

export const useApproveAffiliateApplication = (): UseApproveAffiliateApplicationResult => {
  const queryClient = useQueryClient();

  const {
    mutate,
    isPending,
    isError,
    isSuccess,
    error,
  }: UseMutationResult<AffiliateApplication, Error, ApproveAffiliateApplicationParams> = useMutation({
    mutationFn: async ({ projectId, applicationId, dto }: ApproveAffiliateApplicationParams) => {
      const response = await affiliateApplicationService.approveApplication(projectId, applicationId, dto);
      return transformAffiliateApplication(response);
    },
    onSuccess: (data, variables) => {
      toast.success('Application approved successfully');
      queryClient.invalidateQueries({
        queryKey: ['affiliate-applications', variables.projectId],
      });
      queryClient.invalidateQueries({
        queryKey: ['affiliate-application', variables.projectId, variables.applicationId],
      });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to approve application');
    },
  });

  return {
    approveApplication: mutate,
    isLoading: isPending,
    isError,
    isSuccess,
    error,
  };
};
